import {useCallback} from "react";
import {useDomainNameQuery} from "@/queries.generated";
import {TypenameOmit} from "@/tools";
import {useAuth} from "@/context/AuthProvider";
import {
    useCapabilitySetDomainAllocationQuery,
    useCapabilitySetDomainAllocationSuperuserQuery,
    useDeviceTypeSetDomainAllocationQuery,
    useDeviceTypeSetDomainAllocationSuperuserQuery,
    UserPackageDomainAllocationQuery,
    UserPackageDomainAllocationSuperuserQuery,
    useUserPackageDomainAllocationQuery,
    useUserPackageDomainAllocationSuperuserQuery,
} from "../queries.generated";
import DomainAllocation from "./shared/DomainAllocation";

export type DomainAllocation = TypenameOmit<
    // make superuser fields (totalAllocated, quota) optional as they might not be fetched
    // depending on the user permissions
    UserPackageDomainAllocationQuery["domainAllocation"]["userPackages"][number] &
        Partial<UserPackageDomainAllocationSuperuserQuery["domainAllocation"]["userPackages"][number]>
>;

export type DomainAllocationMatchingSuperuserQuery<Query> = Query extends typeof useUserPackageDomainAllocationQuery
    ? typeof useUserPackageDomainAllocationSuperuserQuery
    : Query extends typeof useCapabilitySetDomainAllocationQuery
      ? typeof useCapabilitySetDomainAllocationSuperuserQuery
      : Query extends typeof useDeviceTypeSetDomainAllocationQuery
        ? typeof useDeviceTypeSetDomainAllocationSuperuserQuery
        : never;

export type DynamicDomainAllocationQuery =
    | typeof useUserPackageDomainAllocationQuery
    | typeof useCapabilitySetDomainAllocationQuery
    | typeof useDeviceTypeSetDomainAllocationQuery;

// Type for the form
export type NewDomainAllocation = {
    newAllocation: number;

    /** How many allocations are automatically included */
    included: number;

    /**
     * Whether this allocation is active or not (useful for detecting under-allocations
     * for ccAattendant for example)
     * */
    active: boolean;
} & DomainAllocation;

// Interface for the form instead of the actual mutation
export type DomainAllocationsData = {
    domainName: string;
    allocations: NewDomainAllocation[];
};

export type DynamicDataSource = {
    loading: boolean;
    refetch: () => Promise<DomainAllocationsData>;
    data: DomainAllocationsData;
};

const allocationIsEmpty = (allocation: DomainAllocation): boolean => {
    const a = structuredClone(allocation);
    delete (a as {name?: string}).name;
    return Object.values(a).filter((v) => v !== 0).length === 0;
};

const createData = (domainAllocations: DomainAllocation[], domainName: string): DomainAllocationsData => {
    return {
        domainName,
        allocations: domainAllocations
            .filter((allocation) => !allocationIsEmpty(allocation))
            .map((allocation) => {
                return {
                    ...allocation,
                    newAllocation: allocation.domainAllocated,
                    included: 0, // no dynamic allocation has included by default
                    active: true, // dynamic allocations can only be active
                };
            }),
    };
};

export function useDynamicDataSource<Q extends DynamicDomainAllocationQuery>(
    domainId: number,
    query: Q,
    superuserQuery: DomainAllocationMatchingSuperuserQuery<Q>,
    queryField: string,
): DynamicDataSource {
    const auth = useAuth();

    // only the superuser can see the system allocation
    const isSuperuser = auth.isSuperuser;
    const allocationQueryHook = isSuperuser ? superuserQuery : query;

    const domainNameQuery = useDomainNameQuery({
        variables: {
            id: domainId,
        },
    });

    const domainAllocationQuery = allocationQueryHook({
        variables: {
            domainId,
        },
        fetchPolicy: "no-cache",
    });

    const refetch = useCallback(async () => {
        const newData = await domainAllocationQuery.refetch();
        const allocations = newData.data.domainAllocation[
            queryField as keyof typeof newData.data.domainAllocation
        ] as unknown as DomainAllocation[];
        return createData(allocations, domainNameQuery.data.domains.items[0].name);
    }, [domainAllocationQuery, domainNameQuery, queryField]);

    if (domainAllocationQuery.loading || domainNameQuery.loading)
        return {
            loading: true,
            refetch,
            data: {
                domainName: "",
                allocations: [],
            },
        };

    return {
        loading: false,
        refetch,
        data: createData(
            domainAllocationQuery.data.domainAllocation[
                queryField as keyof typeof domainAllocationQuery.data.domainAllocation
            ] as unknown as DomainAllocation[],
            domainNameQuery.data.domains.items[0].name,
        ),
    };
}
